import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import RepositoryService from '../../services/RepositoryService';
import LabelsService from '../../services/LabelsService';

const BRAND = '#4daba4';
const BRAND_DARK = '#3d9993';

const statusColor = (status) => (status === 'uploaded' ? 'success' : 'error');
const statusLabel = (status) => (status === 'uploaded' ? 'Cargado' : 'Error');

const getLabelId = (label) => {
  const raw = label?.id ?? label?.Id;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const getLabelName = (label) => label?.name || label?.Name || '';

const normalizeLabel = (label) => {
  if (!label) return null;
  const id = getLabelId(label);
  const name = getLabelName(label).trim();
  if (!name) return null;
  return {
    ...label,
    id: id ?? name,
    name,
  };
};

const BulkDocumentUpload = ({ open, onClose, onCompleted }) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [contentType, setContentType] = useState('document');
  const [category, setCategory] = useState('');
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [labelsError, setLabelsError] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const resetState = () => {
    setFiles([]);
    setContentType('document');
    setCategory('');
    setSelectedLabels([]);
    setDescription('');
    setUploading(false);
    setProgress(0);
    setProgressLabel('');
    setSummary(null);
    setResults([]);
    setError('');
    setLabelsError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const loadLabels = async () => {
      try {
        setLoadingLabels(true);
        setLabelsError('');
        const data = await LabelsService.getAllLabels();
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : [])
          .map(normalizeLabel)
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        setAvailableLabels(list);
      } catch (loadError) {
        console.error('Error al cargar etiquetas:', loadError);
        if (!cancelled) {
          setAvailableLabels([]);
          setLabelsError('No se pudieron cargar las etiquetas oficiales');
        }
      } finally {
        if (!cancelled) setLoadingLabels(false);
      }
    };

    loadLabels();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose?.();
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    setError('');
    setResults([]);
    setSummary(null);
    setProgress(0);
    setProgressLabel('');
    setFiles(selected);
  };

  const accept = contentType === 'audio'
    ? 'audio/*,.mp3,.wav,.m4a,.ogg,.aac'
    : 'application/pdf,.pdf';

  const handleUpload = async () => {
    if (!files.length) {
      setError('Selecciona al menos un archivo');
      return;
    }

    let uploadedBy = '';
    try {
      const raw = localStorage.getItem('usuarioConCedula');
      const parsed = raw ? JSON.parse(raw) : null;
      uploadedBy = parsed?.id ? String(parsed.id) : '';
    } catch {
      uploadedBy = '';
    }

    if (!uploadedBy) {
      setError('No se pudo identificar al usuario. Vuelve a iniciar sesión.');
      return;
    }

    setUploading(true);
    setError('');
    setResults([]);
    setSummary(null);
    setProgress(0);
    setProgressLabel('Subiendo archivos...');

    const labels = selectedLabels
      .map(getLabelName)
      .map((name) => name.trim())
      .filter(Boolean)
      .join(', ');

    try {
      const result = await RepositoryService.bulkUploadDocuments(
        {
          files,
          uploadedBy,
          contentType,
          category,
          labels,
          description,
          isValid: true,
        },
        (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setProgress(percent);
            setProgressLabel(`Subiendo archivos... ${percent}%`);
          }
        }
      );

      const importResults = result.results || result.Results || [];
      setResults(importResults);
      setSummary({
        totalFiles: result.totalFiles ?? result.TotalFiles ?? importResults.length,
        uploaded: result.uploaded ?? result.Uploaded ?? 0,
        errors: result.errors ?? result.Errors ?? 0,
      });
      setProgress(100);
      setProgressLabel('Carga completada');
      onCompleted?.(result);
    } catch (uploadError) {
      const message =
        uploadError?.message
        || uploadError?.response?.data
        || 'Error al cargar los documentos.';
      setError(typeof message === 'string' ? message : 'Error al cargar los documentos.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={600} color={BRAND}>
          Carga masiva de documentos
        </Typography>
        <IconButton onClick={handleClose} disabled={uploading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info">
            Selecciona varios archivos. El título de cada documento será el nombre del archivo (sin extensión).
          </Alert>

          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={contentType}
                label="Tipo"
                disabled={uploading}
                onChange={(e) => {
                  setContentType(e.target.value);
                  setFiles([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                <MenuItem value="document">Documento (PDF)</MenuItem>
                <MenuItem value="audio">Audio</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Categoría (opcional)"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              sx={{ minWidth: 200, flex: 1 }}
            />
          </Box>

          <Autocomplete
            multiple
            options={availableLabels}
            loading={loadingLabels}
            disabled={uploading || Boolean(labelsError)}
            value={selectedLabels}
            onChange={(_, value) => setSelectedLabels(value)}
            getOptionLabel={(option) => getLabelName(option)}
            isOptionEqualToValue={(option, value) =>
              String(getLabelId(option) ?? getLabelName(option))
              === String(getLabelId(value) ?? getLabelName(value))
            }
            filterSelectedOptions
            noOptionsText={
              loadingLabels
                ? 'Cargando etiquetas...'
                : 'No hay etiquetas en el listado oficial'
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={getLabelId(option) ?? getLabelName(option)}
                  size="small"
                  icon={<LocalOfferIcon sx={{ fontSize: '14px !important' }} />}
                  label={getLabelName(option)}
                  sx={{
                    bgcolor: 'rgba(77,171,164,0.12)',
                    color: BRAND_DARK,
                    '& .MuiChip-icon': { color: BRAND },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Etiquetas"
                placeholder={selectedLabels.length ? '' : 'Buscar y seleccionar del listado oficial'}
                helperText={
                  labelsError
                  || 'Solo puedes elegir etiquetas existentes del catálogo oficial'
                }
                error={Boolean(labelsError)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLabels ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Descripción compartida (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
            fullWidth
            multiline
            minRows={2}
          />

          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'rgba(77, 171, 164, 0.4)',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: 'rgba(77, 171, 164, 0.04)',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={accept}
              hidden
              onChange={handleFileChange}
              disabled={uploading}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                textTransform: 'none',
                borderColor: BRAND,
                color: BRAND,
                mb: 1,
              }}
            >
              Seleccionar archivos
            </Button>
            <Typography variant="body2" color="text.secondary">
              {files.length
                ? `${files.length} archivo(s) seleccionado(s)`
                : contentType === 'audio'
                  ? 'Formatos: MP3, WAV, M4A, OGG…'
                  : 'Formato: PDF'}
            </Typography>
            {files.length > 0 && (
              <Box mt={1.5} textAlign="left" maxHeight={120} overflow="auto">
                {files.map((file) => (
                  <Typography key={`${file.name}-${file.size}`} variant="caption" display="block" color="text.secondary">
                    • {file.name} ({Math.round(file.size / 1024)} KB)
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {(uploading || progress > 0) && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {progressLabel}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(77, 171, 164, 0.15)',
                  '& .MuiLinearProgress-bar': { bgcolor: BRAND },
                }}
              />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {summary && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Total: ${summary.totalFiles}`} />
              <Chip color="success" label={`Cargados: ${summary.uploaded}`} />
              <Chip color="error" label={`Errores: ${summary.errors}`} />
            </Stack>
          )}

          {results.length > 0 && (
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Archivo</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row) => {
                    const status = row.status || row.Status;
                    return (
                      <TableRow key={`${row.index || row.Index}-${row.fileName || row.FileName}`}>
                        <TableCell>{row.index ?? row.Index}</TableCell>
                        <TableCell>{row.fileName || row.FileName}</TableCell>
                        <TableCell>{row.title || row.Title || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={statusColor(status)} label={statusLabel(status)} />
                        </TableCell>
                        <TableCell>{row.message || row.Message || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none' }}>
          {summary ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!summary && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
            sx={{
              textTransform: 'none',
              bgcolor: BRAND,
              '&:hover': { bgcolor: BRAND_DARK },
            }}
          >
            {uploading ? 'Subiendo...' : `Subir ${files.length || ''} archivo(s)`}
          </Button>
        )}
        {summary && summary.errors > 0 && (
          <Button
            variant="outlined"
            onClick={() => {
              setSummary(null);
              setResults([]);
              setProgress(0);
              setProgressLabel('');
            }}
            sx={{ textTransform: 'none', borderColor: BRAND, color: BRAND }}
          >
            Intentar de nuevo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkDocumentUpload;
